import React, { useState } from "react";
import { toast } from "react-toastify";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";
import { MAX_FEATURED } from "./model";

const SourceLine = ({ source }) =>
  source === "roster"
    ? "Principal broker or office manager on the MLS roster"
    : source === "staff" || source === "gitcha"
      ? "Added by Gitcha"
      : "Invited";

const Picker = ({ id, placeholder, options, disabled, onPick, onClose }) => (
  <div className="brokerage-studio__picker">
    <SelectDropDown
      id={id}
      className="brokerage-studio__select"
      classNamePrefix="select"
      placeholder={placeholder}
      options={options}
      value={null}
      disabled={disabled}
      styles={PICKER_STYLES}
      errorClass="d-none"
      onChange={(option) => option && onPick(option.value)}
    />
    <button type="button" className="brokerage-studio__link" onClick={onClose}>
      Cancel
    </button>
  </div>
);

const PAGE_SIZE = 25;

const Switch = ({ label, checked, disabled, onChange }) => (
  <label className={`brokerage-studio__switch-row${disabled ? " is-disabled" : ""}`}>
    <input
      type="checkbox"
      role="switch"
      className="brokerage-studio__switch-input"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className="brokerage-studio__switch-track" aria-hidden="true" />
    <span>{label}</span>
  </label>
);

export const orderAgents = (agents, featured) => {
  const byId = Object.fromEntries(agents.map((agent) => [agent.userId, agent]));
  const top = featured.filter((userId) => byId[userId]).map((userId) => byId[userId]);
  const rest = agents
    .filter((agent) => !featured.includes(agent.userId))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...top, ...rest];
};

export const AgentsOnPage = ({ data, saveFeatured, saveHidden }) => {
  const [find, setFind] = useState("");
  const [shown, setShown] = useState(PAGE_SIZE);
  const agents = data.agents || [];
  const known = new Set(agents.map((agent) => agent.userId));
  const featured = data.featured.filter((userId) => known.has(userId));
  const hidden = (data.hidden || []).filter((userId) => known.has(userId));
  const busy = saveFeatured.isPending || saveHidden.isPending;
  const term = find.trim().toLowerCase();
  const ordered = orderAgents(agents, featured).filter((agent) => !term || agent.name.toLowerCase().includes(term));
  const visible = ordered.slice(0, shown);
  const fail = (error) => toast.error(error.message);

  const setFeaturedOrder = (next) => saveFeatured.mutate(next, { onError: fail });

  const move = (userId, step) => {
    const index = featured.indexOf(userId);
    const target = index + step;
    if (target < 0 || target >= featured.length) return;
    const next = [...featured];
    next.splice(index, 1);
    next.splice(target, 0, userId);
    setFeaturedOrder(next);
  };

  const setFeaturedFor = (userId, on) =>
    setFeaturedOrder(on ? [...featured, userId] : featured.filter((id) => id !== userId));

  const setShownFor = (userId, on) =>
    saveHidden.mutate(on ? hidden.filter((id) => id !== userId) : [...hidden, userId], { onError: fail });

  return (
    <section className="content-block">
      <div className="title-sec">
        <h4>Agents on your page</h4>
      </div>
      <p className="brokerage-studio__hint">
        Everyone on the {data.networkName || "MLS"} roster at {data.name}. Up to {MAX_FEATURED} featured agents show
        first, in this order. Turn off Shown for anyone who should not appear, like an&nbsp;assistant.
      </p>
      {agents.length > 8 ? (
        <input
          className="brokerage-studio__find"
          type="search"
          value={find}
          onChange={(event) => {
            setFind(event.target.value);
            setShown(PAGE_SIZE);
          }}
          placeholder="Find an agent"
          aria-label="Find an agent"
        />
      ) : null}
      <ul className="brokerage-studio__list brokerage-studio__agents">
        {visible.map((agent) => {
          const isFeatured = featured.includes(agent.userId);
          const isHidden = hidden.includes(agent.userId);
          const position = featured.indexOf(agent.userId);
          return (
            <li key={agent.userId} className={isHidden ? "is-hidden" : ""}>
              <span className="brokerage-studio__name">
                {agent.name}
                {isHidden ? <span>Not on your page</span> : null}
              </span>
              <span className="brokerage-studio__row-tools">
                {isFeatured && featured.length > 1 ? (
                  <>
                    <button
                      type="button"
                      className="brokerage-studio__link"
                      aria-label={`Move ${agent.name} up`}
                      disabled={position === 0 || busy}
                      onClick={() => move(agent.userId, -1)}
                    >
                      Up
                    </button>
                    <button
                      type="button"
                      className="brokerage-studio__link"
                      aria-label={`Move ${agent.name} down`}
                      disabled={position === featured.length - 1 || busy}
                      onClick={() => move(agent.userId, 1)}
                    >
                      Down
                    </button>
                  </>
                ) : null}
                <Switch
                  label="Featured"
                  checked={isFeatured}
                  disabled={busy || isHidden || (!isFeatured && featured.length >= MAX_FEATURED)}
                  onChange={(on) => setFeaturedFor(agent.userId, on)}
                />
                <Switch
                  label="Shown"
                  checked={!isHidden}
                  disabled={busy}
                  onChange={(on) => setShownFor(agent.userId, on)}
                />
              </span>
            </li>
          );
        })}
      </ul>
      {!ordered.length && term ? <p className="brokerage-studio__hint">No agent named &ldquo;{find.trim()}&rdquo; on your&nbsp;roster.</p> : null}
      {ordered.length > shown ? (
        <button type="button" className="brokerage-studio__link brokerage-studio__more" onClick={() => setShown(shown + PAGE_SIZE)}>
          Show more agents
        </button>
      ) : null}
    </section>
  );
};

export const PageEditors = ({ data, addManager, removeManager }) => {
  const [adding, setAdding] = useState(false);
  return (
    <section className="content-block">
      <div className="title-sec">
        <h4>Who can edit this page</h4>
        {data.invitable.length && !adding ? (
          <a role="button" tabIndex={0} onClick={() => setAdding(true)} onKeyDown={(event) => event.key === "Enter" && setAdding(true)}>
            Add someone
          </a>
        ) : null}
      </div>
      <p className="brokerage-studio__hint">
        Principal brokers and office managers on the roster always can. People you add lose access if they leave
        the&nbsp;roster.
      </p>
      <ul className="brokerage-studio__list">
        {data.admins.map((admin) => (
          <li key={`${admin.source}-${admin.userId}`}>
            <span className="brokerage-studio__name">
              {admin.name}
              <span>{SourceLine({ source: admin.source })}</span>
            </span>
            {admin.source === "broker" || admin.source === "invite" ? (
              <span className="brokerage-studio__row-tools">
                <button
                  type="button"
                  className="brokerage-studio__link"
                  aria-label={`Remove ${admin.name}`}
                  disabled={removeManager.isPending}
                  onClick={() => removeManager.mutate(admin.userId, { onError: (error) => toast.error(error.message) })}
                >
                  Remove
                </button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {adding && data.invitable.length ? (
        <Picker
          id="brokerage-add-editor"
          placeholder="Search people in your office"
          options={data.invitable.map((member) => ({ value: member.userId, label: member.name }))}
          disabled={addManager.isPending}
          onPick={(userId) => {
            setAdding(false);
            addManager.mutate(userId, {
              onSuccess: () => toast.success("They can now edit your brokerage page."),
              onError: (error) => toast.error(error.message),
            });
          }}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </section>
  );
};
